import type { CSSProperties } from 'react';

import type { FocalPoint } from '@/features/storefront/sections/schema';

/**
 * The inline style that holds a picture's chosen point in view.
 *
 * `object-position` does the work for any `object-cover` image: the point at
 * `x% y%` of the picture sits at `x% y%` of the frame, so whatever shape the
 * frame is - a wide monitor, a phone - that part of the photo is the part
 * kept. Zoom scales around the same point, so enlarging never drifts away
 * from what the merchant picked; the frame around the image must clip.
 *
 * `undefined` for no point, so a picture nobody has repositioned renders with
 * no inline style at all - exactly as it did before this existed.
 */
export function focalStyle(focal: FocalPoint | null | undefined): CSSProperties | undefined {
  if (!focal) return undefined;
  const at = `${round(focal.x)}% ${round(focal.y)}%`;
  const phone = focal.zoom_phone ?? focal.zoom;
  if (phone !== focal.zoom) {
    // A banner zoomed differently on a phone. An inline style cannot hold a
    // media query, so the phone's zoom rides along as a custom property and
    // the rule in `index.css` swaps it in below `sm` - the same breakpoint at
    // which a banner stops being a share of the window and keeps a fixed
    // height, which is the switch between the two shapes the merchant saw.
    return {
      objectPosition: at,
      transform: `scale(var(--focal-zoom-now, ${round(focal.zoom)}))`,
      transformOrigin: at,
      '--focal-zoom-phone': String(round(phone)),
    } as CSSProperties;
  }
  return focal.zoom > 1
    ? { objectPosition: at, transform: `scale(${focal.zoom})`, transformOrigin: at }
    : { objectPosition: at };
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}
